import { useEffect, useRef, useState } from "react";
import { Ban, CornerUpLeft, Edit3, File as FileIcon, ImageIcon, ListChecks, Mic, Paperclip, Send, Smile, Trash2, X } from "lucide-react";
import type { Member, Message, Room } from "./types";
import { Avatar, useChat } from "./ui";
import { fmtDuration, fmtSize } from "./jalali";

const EMOJIS = "😀 😂 🤣 😊 😍 😘 😎 🤔 😅 😉 🙏 👍 👎 👏 🙌 💪 ✅ ❌ ⭐ 🔥 🎉 ❤️ 💙 😢 😭 😡 😮 🤝 👌 ✌️ 📎 📌 📅 ⏰ 💰 📈 📉 📄 🧾 ✔️ ⚠️ 🚀 👀 🙂 😴 🤷 🙈".split(" ");

export interface SendPayload { text: string; mentions: number[] }
interface Props {
  room: Room; members: Member[]; replyTo: Message | null; editing: Message | null; blocked: string | null;
  files: File[]; setFiles: (f: File[]) => void;
  onSend: (p: SendPayload) => void; onEdit: (p: SendPayload) => void;
  onVoice: (blob: Blob, seconds: number, wave: number[]) => void;
  onTyping: () => void; onCancelReply: () => void; onCancelEdit: () => void;
}

export default function Composer({ room, members, replyTo, editing, blocked, files, setFiles, onSend, onEdit, onVoice, onTyping, onCancelReply, onCancelEdit }: Props) {
  const { me, toast, newTask } = useChat();
  const [text, setText] = useState("");
  const [menu, setMenu] = useState<"" | "attach" | "emoji">("");
  const [mq, setMq] = useState<string | null>(null);
  const [mIdx, setMIdx] = useState(0);
  const mentions = useRef<Map<number, string>>(new Map());
  const ta = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null); const imgRef = useRef<HTMLInputElement>(null);
  const [rec, setRec] = useState<{ sec: number } | null>(null);
  const recRef = useRef<{ mr: MediaRecorder; chunks: Blob[]; stream: MediaStream; ctx: AudioContext; samples: number[]; timer: number; sampler: number; cancelled: boolean; start: number } | null>(null);

  useEffect(() => { setText(editing?.content || ""); mentions.current = new Map<number, string>((editing?.mentions || []).map(m => [m.id, m.name] as [number, string])); if (editing) ta.current?.focus(); }, [editing]);
  useEffect(() => { if (replyTo) ta.current?.focus(); }, [replyTo]);
  useEffect(() => { const t = ta.current; if (t) { t.style.height = "auto"; t.style.height = Math.min(140, t.scrollHeight) + "px"; } }, [text]);
  useEffect(() => () => { recRef.current && cleanupRec(true); }, []); // eslint-disable-line

  const cands = room.type === "group" && mq !== null ? members.filter(m => m.id !== me.id && m.name.includes(mq)).slice(0, 6) : [];

  const onChange = (v: string) => {
    setText(v); onTyping();
    const caret = ta.current?.selectionStart ?? v.length;
    const m = /(^|\s)@([^\s@]{0,20})$/.exec(v.slice(0, caret));
    setMq(m ? m[2] : null); setMIdx(0);
  };
  const pickMention = (m: Member) => {
    const caret = ta.current?.selectionStart ?? text.length;
    const before = text.slice(0, caret).replace(/@([^\s@]{0,20})$/, `@${m.name} `);
    const next = before + text.slice(caret);
    mentions.current.set(m.id, m.name); setText(next); setMq(null);
    requestAnimationFrame(() => { ta.current?.focus(); ta.current?.setSelectionRange(before.length, before.length); });
  };
  const collect = (): SendPayload => ({ text: text.trim(), mentions: Array.from(mentions.current).filter(([, n]) => text.includes("@" + n)).map(([id]) => id) });
  const submit = () => {
    if (blocked) return;
    const p = collect();
    if (editing) { if (p.text) { onEdit(p); setText(""); } return; }
    if (!p.text && !files.length) return;
    onSend(p); setText(""); setMq(null); mentions.current = new Map(); setMenu("");
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (cands.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMIdx(i => (i + 1) % cands.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMIdx(i => (i - 1 + cands.length) % cands.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pickMention(cands[mIdx]); return; }
      if (e.key === "Escape") { setMq(null); return; }
    }
    if (e.key === "Escape" && editing) onCancelEdit();
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); }
  };
  const onPaste = (e: React.ClipboardEvent) => {
    const f = Array.from(e.clipboardData.files); if (f.length) { e.preventDefault(); setFiles([...files, ...f]); }
  };
  const addFiles = (list: FileList | null) => { if (list?.length) setFiles([...files, ...Array.from(list)]); setMenu(""); };
  const insertEmoji = (e: string) => { const t = ta.current; const p = t?.selectionStart ?? text.length; setText(text.slice(0, p) + e + text.slice(p)); requestAnimationFrame(() => { t?.focus(); t?.setSelectionRange(p + e.length, p + e.length); }); };

  // ─── ضبط ویس با موج صوتی ───
  const cleanupRec = (cancel: boolean) => {
    const r = recRef.current; if (!r) return; r.cancelled = cancel;
    clearInterval(r.timer); clearInterval(r.sampler);
    if (r.mr.state !== "inactive") r.mr.stop();
    r.stream.getTracks().forEach(t => t.stop()); r.ctx.close().catch(() => {});
    if (cancel) recRef.current = null;
  };
  const startRec = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast("مرورگر از ضبط صدا پشتیبانی نمی‌کند (نیاز به HTTPS)", false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find(m => MediaRecorder.isTypeSupported(m)) || "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const an = ctx.createAnalyser(); an.fftSize = 256; ctx.createMediaStreamSource(stream).connect(an);
      const buf = new Uint8Array(an.fftSize); const samples: number[] = [];
      const state = { mr, chunks: [] as Blob[], stream, ctx, samples, cancelled: false, start: Date.now(), timer: 0, sampler: 0 };
      mr.ondataavailable = e => e.data.size && state.chunks.push(e.data);
      mr.onstop = () => {
        const r = recRef.current; if (!r || r.cancelled) return;
        const sec = Math.max(1, Math.round((Date.now() - r.start) / 1000));
        const blob = new Blob(r.chunks, { type: mr.mimeType || "audio/webm" });
        const N = 40; const wave = Array.from({ length: N }, (_, i) => { const a = Math.floor((i * r.samples.length) / N), b = Math.max(a + 1, Math.floor(((i + 1) * r.samples.length) / N)); const seg = r.samples.slice(a, b); return Math.round(Math.min(100, Math.max(12, (seg.length ? Math.max(...seg) : 0.1) * 160))); });
        recRef.current = null; setRec(null); onVoice(blob, sec, wave);
      };
      state.sampler = window.setInterval(() => { an.getByteTimeDomainData(buf); let m = 0; for (let i = 0; i < buf.length; i++) m = Math.max(m, Math.abs(buf[i] - 128) / 128); samples.push(m); }, 100);
      state.timer = window.setInterval(() => { const s = Math.round((Date.now() - state.start) / 1000); setRec({ sec: s }); if (s >= 300) cleanupRec(false); }, 250);
      recRef.current = state; mr.start(250); setRec({ sec: 0 });
    } catch { toast("دسترسی به میکروفون داده نشد", false); }
  };

  if (blocked) return <div className="cx-composer blocked"><Ban size={16} /> {blocked}</div>;

  if (rec) return (
    <div className="cx-composer rec">
      <button className="cx-icon-btn danger" onClick={() => { cleanupRec(true); setRec(null); }} aria-label="لغو"><Trash2 size={19} /></button>
      <div className="cx-rec-live"><i /> در حال ضبط <b>{fmtDuration(rec.sec)}</b></div>
      <button className="cx-send" onClick={() => cleanupRec(false)} aria-label="ارسال ویس"><Send size={18} /></button>
    </div>
  );

  return (
    <div className="cx-composer-wrap">
      {(replyTo || editing) && (
        <div className={`cx-compose-banner ${editing ? "edit" : ""}`}>
          {editing ? <Edit3 size={16} /> : <CornerUpLeft size={16} />}
          <div><b>{editing ? "ویرایش پیام — تاریخچه برای همه ثبت می‌شود" : `پاسخ به ${replyTo!.sender_name}${replyTo!.room_id !== room.id ? " (پاسخ خصوصی)" : ""}`}</b>
            <span>{editing ? editing.content : replyTo!.type === "voice" ? "🎤 پیام صوتی" : replyTo!.type === "image" ? "🖼 عکس" : replyTo!.type === "file" ? "📎 " + replyTo!.file_name : replyTo!.content}</span></div>
          <button className="cx-icon-btn" onClick={editing ? onCancelEdit : onCancelReply}><X size={16} /></button>
        </div>
      )}
      {files.length > 0 && (
        <div className="cx-staged">
          {files.map((f, i) => (
            <div key={i} className="cx-stage-item">
              {f.type.startsWith("image/") ? <img src={URL.createObjectURL(f)} alt="" onLoad={e => URL.revokeObjectURL((e.target as HTMLImageElement).src)} /> : <span className="ic"><FileIcon size={22} /></span>}
              <div><b>{f.name}</b><small>{fmtSize(f.size)}</small></div>
              <button className="cx-icon-btn" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="cx-composer">
        {cands.length > 0 && (
          <div className="cx-mention-pop">{cands.map((m, i) => <button key={m.id} className={i === mIdx ? "on" : ""} onMouseDown={e => { e.preventDefault(); pickMention(m); }}><Avatar name={m.name} src={m.avatar} size={26} /> {m.name}</button>)}</div>
        )}
        {menu === "emoji" && <div className="cx-pop emoji">{EMOJIS.map(e => <button key={e} onClick={() => insertEmoji(e)}>{e}</button>)}</div>}
        {menu === "attach" && (
          <div className="cx-pop attach">
            <button onClick={() => imgRef.current?.click()}><span style={{ background: "#7048e8" }}><ImageIcon size={18} /></span>عکس و ویدیو</button>
            <button onClick={() => fileRef.current?.click()}><span style={{ background: "#2f5bea" }}><FileIcon size={18} /></span>فایل و سند</button>
            <button onClick={() => { setMenu(""); newTask({ room_id: room.id, assignee_id: room.type === "direct" ? room.other_id || undefined : undefined }); }}><span style={{ background: "#0ca678" }}><ListChecks size={18} /></span>ثبت وظیفه</button>
          </div>
        )}
        <input ref={imgRef} type="file" accept="image/*,video/*" multiple hidden onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={fileRef} type="file" multiple hidden onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
        <button className={`cx-icon-btn ${menu === "emoji" ? "on" : ""}`} onClick={() => setMenu(menu === "emoji" ? "" : "emoji")} aria-label="ایموجی"><Smile size={21} /></button>
        <button className={`cx-icon-btn ${menu === "attach" ? "on" : ""}`} onClick={() => setMenu(menu === "attach" ? "" : "attach")} aria-label="پیوست"><Paperclip size={20} /></button>
        <textarea ref={ta} rows={1} value={text} placeholder={files.length ? "توضیحات (اختیاری)…" : "پیام بنویسید…   (@ برای منشن)"} onChange={e => onChange(e.target.value)} onKeyDown={onKey} onPaste={onPaste} dir="auto" />
        {text.trim() || files.length || editing
          ? <button className="cx-send" onClick={submit} aria-label="ارسال"><Send size={18} /></button>
          : <button className="cx-send mic" onClick={startRec} aria-label="ضبط ویس"><Mic size={19} /></button>}
      </div>
    </div>
  );
}
